import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CONSENT_OPT_OUT_VALUE, resolveAdOptOut } from '@ecom/common';
import { PrivacyCheckService } from '../privacy-check/privacy-check.service';
import type { ConsentPreferences, NavigatorWithGPC } from './analytics.types';

/**
 * Handles analytics consent and privacy control logic.
 * Manages consent preferences, Global Privacy Control (GPC) detection,
 * and integration with the PrivacyCheckService.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsConsentService {
  constructor(
    private readonly privacyCheckService: PrivacyCheckService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  /**
   * Get current consent preferences from cookie
   * @returns Consent preferences with dataSharing flag and cookie value
   */
  getConsentPreferences(): ConsentPreferences {
    const cookieValue = this.privacyCheckService.getPrivacyCookie();

    // Cookie value: '0' = allow data sharing, '1' = do not sell data, null = no preference.
    // GPC overrides a stored '0' (see isOptedOut), so dataSharing is false whenever the visitor
    // is opted out by either signal.
    return {
      dataSharing: !resolveAdOptOut(cookieValue, this.hasGlobalPrivacyControl()),
      cookieValue,
    };
  }

  /**
   * True when the visitor opted out of sale/sharing: a stored choice other than allow ('0'), or
   * Global Privacy Control. Ad destinations must not receive their data.
   * Always false on the server, where neither signal is readable.
   */
  isOptedOut(): boolean {
    return !this.getConsentPreferences().dataSharing;
  }

  /**
   * Check if browser has Global Privacy Control enabled
   * @returns True if GPC is enabled
   */
  hasGlobalPrivacyControl(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      return !!(window.navigator && (window.navigator as NavigatorWithGPC).globalPrivacyControl);
    } catch {
      return false;
    }
  }

  /**
   * Handle Global Privacy Control by persisting the opt-out ('1') whenever GPC is on and the stored
   * choice is not already '1'. A stored '0' is overwritten: GPC wins over it, and cookie-only readers
   * (servers, other subdomains) must see the opt-out.
   */
  handleGlobalPrivacyControl(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const hasGPC = this.hasGlobalPrivacyControl();
    const alreadyOptedOut = this.privacyCheckService.getPrivacyCookie() === CONSENT_OPT_OUT_VALUE;

    if (hasGPC && !alreadyOptedOut) {
      // Respect Global Privacy Control signal by blocking data sharing
      // Set cookie to '1' = do not sell data, following GPC standard
      this.privacyCheckService.updatePrivacyCheck(1);
    }
  }
}
